import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", message: "" });

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const send = async () => {
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Add your name and a message so Nick can help.");
      return;
    }
    setSending(true);
    try {
      await api.post("/chat", form);
      setSent(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setOpen(false);
    setTimeout(() => { setSent(false); setForm({ name: "", phone: "", message: "" }); }, 250);
  };

  return (
    <div className="fixed bottom-5 right-5 z-[60] flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            data-testid="chat-panel"
            className="w-[88vw] max-w-sm rounded-3xl bg-white border border-nick-border shadow-2xl overflow-hidden"
          >
            <div className="bg-nick-primary px-5 py-4 flex items-center justify-between">
              <div>
                <div className="text-white font-display font-bold text-lg leading-tight">Message Nick</div>
                <div className="text-white/80 text-xs">Tell him what you're planning — he'll text you back.</div>
              </div>
              <button data-testid="chat-close" onClick={reset} className="text-white/90 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            {sent ? (
              <div className="p-6 text-center" data-testid="chat-success">
                <div className="mx-auto h-14 w-14 rounded-2xl bg-nick-primary/10 text-nick-primary flex items-center justify-center">
                  <Check className="h-7 w-7" />
                </div>
                <h3 className="mt-4 font-display font-extrabold text-xl">Message sent!</h3>
                <p className="mt-2 text-sm text-nick-muted">
                  Nick got your message and will reach out soon. Talk to you shortly!
                </p>
                <Button onClick={reset} className="mt-5 rounded-full bg-nick-dark hover:bg-black text-white font-bold px-6">
                  Done
                </Button>
              </div>
            ) : (
              <div className="p-5 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-nick-muted">Your name</Label>
                    <Input data-testid="chat-name" value={form.name} onChange={(e) => set({ name: e.target.value })}
                      placeholder="First name" className="mt-1 h-11 rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-nick-muted">Phone</Label>
                    <Input data-testid="chat-phone" type="tel" value={form.phone} onChange={(e) => set({ phone: e.target.value })}
                      placeholder="So Nick can reply" className="mt-1 h-11 rounded-xl" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-nick-muted">Message</Label>
                  <Textarea data-testid="chat-message" value={form.message} onChange={(e) => set({ message: e.target.value })}
                    placeholder="Hey Nick! I'm thinking about a…" className="mt-1 rounded-xl min-h-[90px]" />
                </div>
                <Button data-testid="chat-send" onClick={send} disabled={sending}
                  className="w-full h-12 rounded-full bg-nick-primary hover:bg-nick-primary-hover text-white font-bold">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Send to Nick</>}
                </Button>
                <p className="text-[11px] text-nick-muted text-center">
                  Your message goes straight to Nick. No bots, no spam.
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        data-testid="chat-toggle"
        onClick={() => (open ? reset() : setOpen(true))}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
        className="flex items-center gap-2 rounded-full bg-nick-primary hover:bg-nick-primary-hover text-white font-bold pl-4 pr-5 py-3.5 shadow-xl transition-colors"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
        <span className="text-sm">{open ? "Close" : "Chat with Nick"}</span>
      </motion.button>
    </div>
  );
}
